import {
    createContext,
    type ReactElement,
    type ReactNode,
    useContext,
    useMemo
} from "react";

import type {
    AgreementEnvelopeView,
    OnboardingApplicationView,
    OnboardingStepName,
    OnboardingStepView
} from "@/services/clientOnboarding";

interface OnboardingApplicationContextValue {
    application: OnboardingApplicationView;
    steps: OnboardingStepView[];
    /** The latest envelope per agreement type, as the bundle carries it.
     *  Empty until the issuer's approval sends them. */
    agreements: AgreementEnvelopeView[];
    stepsByName: Partial<Record<OnboardingStepName, OnboardingStepView>>;
    applyStepUpdate: (update: {
        application: OnboardingApplicationView;
        step: OnboardingStepView;
    }) => void;
    refetch: () => Promise<void>;
}

const Context = createContext<OnboardingApplicationContextValue | null>(null);

export function OnboardingApplicationProvider({
    application,
    steps,
    agreements = [],
    applyStepUpdate,
    refetch,
    children
}: {
    application: OnboardingApplicationView;
    steps: OnboardingStepView[];
    /** Optional so a caller with an older bundle (or a test that does not
     *  care about agreements) still mounts. */
    agreements?: AgreementEnvelopeView[];
    applyStepUpdate: OnboardingApplicationContextValue["applyStepUpdate"];
    refetch: OnboardingApplicationContextValue["refetch"];
    children: ReactNode;
}): ReactElement {
    const stepsByName = useMemo<
        OnboardingApplicationContextValue["stepsByName"]
    >(() => {
        const map: OnboardingApplicationContextValue["stepsByName"] = {};
        for (const step of steps) {
            map[step.stepName] = step;
        }
        return map;
    }, [steps]);

    const value = useMemo<OnboardingApplicationContextValue>(
        () => ({
            application,
            steps,
            agreements,
            stepsByName,
            applyStepUpdate,
            refetch
        }),
        [application, steps, agreements, stepsByName, applyStepUpdate, refetch]
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

/**
 * Read the live application + per-step state inside the hub. Throws when
 * called outside an `OnboardingApplicationProvider` — this surfaces a
 * mount-order mistake immediately rather than letting forms silently
 * write to `console.log`.
 */
export function useOnboardingApplicationContext(): OnboardingApplicationContextValue {
    const value = useContext(Context);
    if (!value) {
        throw new Error(
            "useOnboardingApplicationContext must be used within <OnboardingApplicationProvider>."
        );
    }
    return value;
}

/**
 * Variant that returns `null` outside a provider — useful for the
 * mock-scenario dev path (the `?scenario=N` query param renders without
 * a live application bundle, and the wired forms downgrade to their
 * console.log behavior automatically).
 */
export function useOnboardingApplicationContextOrNull(): OnboardingApplicationContextValue | null {
    return useContext(Context);
}
